import { useMutation } from '@tanstack/react-query';

import type { VacancyWithIndeedJob } from 'clients/AutoCompleteClient/types';

import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { queryClient } from 'config/queryClient';
import {
  calculateFormCompletionPercentage,
  mapJobFormValuesToJobImprovedNestedInput,
  mapJobImprovedGetDetailOutputToJobFormValues,
} from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/utils/EnrichVacancyModal.mappers';
import { QueryKey } from 'types/query';

export const useAutoProcessIndeedVacancy = () => {
  return useMutation(
    async (vacancy: VacancyWithIndeedJob): Promise<VacancyWithIndeedJob> => {
      if (!vacancy.originalIndeedJob) {
        throw Error('Vacancy has no original Indeed job');
      }

      const { data: jobImprovedDraft } = await reachoutClient.processVacancy({
        input_type: 'text',
        input_text: vacancy.originalIndeedJob.description,
      });

      const jobFormValues = mapJobImprovedGetDetailOutputToJobFormValues(jobImprovedDraft);
      const nestedValues = mapJobFormValuesToJobImprovedNestedInput(jobFormValues);

      if (!nestedValues.job_details?.job_title) {
        nestedValues.job_details = {
          job_title: vacancy.name,
        };
      }

      nestedValues.job_indeed_id = vacancy.job_indeed_id;
      nestedValues.origin_job_title = vacancy.origin_job_title ?? vacancy.job_title ?? undefined;
      nestedValues.origin_text = vacancy.originalIndeedJob.description;

      const formCompletionPercentage = calculateFormCompletionPercentage(jobFormValues);
      if (formCompletionPercentage > (nestedValues?.quality_percent ?? 0)) {
        nestedValues.quality_percent = formCompletionPercentage;
      }

      const { data: jobImproved } = await reachoutClient.createJobImproved({ ...nestedValues, job_preview: '' });

      return {
        job_indeed_id: vacancy.job_indeed_id,
        name: vacancy.name,
        value: vacancy.value,
        type: 'INDEED',
        originalIndeedJob: vacancy.originalIndeedJob,
        is_improved: true,

        db_id: jobImproved.id,
        origin_job_title: jobImproved.origin_job_title,
        quality_percent: jobImproved.quality_percent,
        created_at: jobImproved.created_at,
        updated_at: jobImproved.updated_at,
      };
    },
    {
      onSuccess: ({ db_id }) => {
        queryClient.removeQueries([QueryKey.useEnrichedVacanciesQuery], { exact: false });
        queryClient.removeQueries([QueryKey.useIndeedJobListQuery], { exact: false });
        queryClient.resetQueries([QueryKey.useGetJobImproved, { db_id }], {
          exact: false,
        });
      },
    },
  );
};
